/**
 * useHierarchyAccess.js
 *
 * Custom hook that provides access-control utilities for hierarchy-gated UI.
 *
 * Usage:
 *   const { canView, canManage, requireLevel } = useHierarchyAccess();
 *
 *   if (!canView('department')) return <AccessDenied />;
 */

import { useCallback } from 'react';
import { useHierarchy } from '../context/HierarchyContext';

/**
 * @returns {{
 *   canView: (level: string) => boolean,
 *   canManage: (level: string) => boolean,
 *   isWithinScope: (assessment: object) => boolean,
 *   requireLevel: (level: string, fallback?: string) => string,
 * }}
 */
const useHierarchyAccess = () => {
  const {
    canViewLevel,
    canManageLevel,
    filterAssessmentsByScope,
    hierarchyLevel,
    HIERARCHY_LEVELS,
  } = useHierarchy();

  /** True if the user may view the given hierarchy level. */
  const canView = useCallback(
    (level) => canViewLevel(level),
    [canViewLevel],
  );

  /** True if the user may create/edit assessments at the given level. */
  const canManage = useCallback(
    (level) => canManageLevel(level),
    [canManageLevel],
  );

  /**
   * Returns true if a single assessment is visible under the current scope.
   * Uses the same logic as filterAssessmentsByScope (single-item optimisation).
   */
  const isWithinScope = useCallback(
    (assessment) => {
      if (!assessment) return false;
      const result = filterAssessmentsByScope([assessment]);
      return result.length > 0;
    },
    [filterAssessmentsByScope],
  );

  /**
   * If the user cannot view the requested level, returns the highest level they
   * CAN view (or the optionally provided fallback).
   *
   * @param {string} level - The desired hierarchy level
   * @param {string} [fallback] - Fallback if level is denied
   * @returns {string} Level the user is permitted to view
   */
  const requireLevel = useCallback(
    (level, fallback) => {
      if (canViewLevel(level)) return level;
      if (fallback && canViewLevel(fallback)) return fallback;
      // Return the highest accessible level
      const orderedLevels = [
        HIERARCHY_LEVELS.ENTERPRISE,
        HIERARCHY_LEVELS.DEPARTMENT,
        HIERARCHY_LEVELS.DISCIPLINE,
      ];
      return (
        orderedLevels.find((l) => canViewLevel(l)) ||
        HIERARCHY_LEVELS.ENTERPRISE
      );
    },
    [canViewLevel, HIERARCHY_LEVELS],
  );

  return {
    canView,
    canManage,
    isWithinScope,
    requireLevel,
    currentLevel: hierarchyLevel,
  };
};

export default useHierarchyAccess;
